import React, { useRef, useState } from 'react';
import { connect } from 'react-redux';
import {
  arrayMove,
  SortableContainer,
  SortableElement,
  SortableHandle
} from 'react-sortable-hoc';
import FontAwesome from 'react-fontawesome';
import PropTypes from 'prop-types';
import { BillingCategoryType } from 'propTypes';
import LinkButton from 'components/LinkButton/LinkButton';
import { billingCategoryGroups, modals } from 'ducks';
import FALabel from 'components/shared/FALabel';
import BillingCategoryDetails from './BillingCategoryDetails';

import './styles/BillingCategoryGroups.scss';

const BillingCategoryGroupContainer = SortableContainer((props) => {
  const {
    categories,
    newCategory,
    helperContainerRef
  } = props;

  const [editing, setEditing] = useState(null);

  return (
    <div ref={helperContainerRef}>
      {
        categories.map((category, index) => (
          <BillingCategoryGroup
            key={category.id}
            index={index}
            category={category}
            editing={editing}
            setEditing={setEditing}
            {...props}
          />
        ))
      }
      {
        newCategory && (
          <BillingCategoryGroup
            key='NEW_CATEGORY'
            index={categories.length}
            category={newCategory}
            {...props}
          />
        )
      }
    </div>
  );
});

const CreateActions = ({ onSave, onCancel }) => (
  <div>
    <LinkButton
      onClick={onCancel}
      content={<FALabel icon='minus-circle' label='Cancel' className='text-danger' />}
    />
    <LinkButton
      onClick={onSave}
      content={<FALabel icon='check-circle' label='Save' />}
    />
  </div>
);

CreateActions.propTypes = {
  onSave: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired
};

const EditActions = ({
  active,
  expanded,
  onToggleActive,
  onToggleEdit,
  categoryId
}) => (
  <div>
    <LinkButton
      onClick={onToggleActive}
      content={
        active
          ? <FALabel icon='eye-slash' label='Make Inactive' className='text-danger' />
          : <FALabel icon='eye' label='Activate' />
      }
    />
    {onToggleEdit && (
      <LinkButton
        onClick={onToggleEdit}
        className={"edit-" + categoryId}
        content={
          expanded
            ? <FALabel icon='chevron-up' label='Close' />
            : <FALabel icon='chevron-down' label='Edit' />
        }
      />
    )}
  </div>
);

EditActions.propTypes = {
  active: PropTypes.bool.isRequired,
  expanded: PropTypes.bool,
  onToggleActive: PropTypes.func.isRequired,
  onToggleEdit: PropTypes.func
};

EditActions.defaultProps = {
  expanded: false,
  onToggleEdit: null
};

const BillingCategoryGroup = SortableElement((props) => {
  const {
    category,
    editing,
    setEditing,
    confirmDeactivate,
    saveCategory,
    cancelNewCategory
  } = props;

  const onToggleActive = () => {
    if (category.active) {
      // confirm deactivate
      const title = 'Confirmation';
      const message = (
        <div>
          <p>{`By marking this category group "${category.name}" as inactive, the following will occur:`}</p>
          <ul>
            <li>Previous ledger items will remain accessible.</li>
            <li>All categories in this group will be marked inactive.</li>
            <li>All categories in this group will be removed from the quick action modals and filters.</li>
            <li>Contracts with fees that reference inactive categories cannot be submitted.</li>
          </ul>
          <h1>Important</h1>
          <p>Please update any current contract fees associated to categories in this group.</p>
        </div>
      );
      const onConfirm = () => saveCategory(Object.assign({}, category, { active: false }));

      confirmDeactivate(message, title, onConfirm);
    } else {
      // activate
      saveCategory(Object.assign({}, category, { active: true }));
    }
  };

  // if existing group
  if (category.id) {
    const expanded = category.id === editing;
    return (
      <div className={expanded ? 'group-expanded' : 'group-collapsed'}>
        <div className={expanded ? 'group-row detail-header' : 'group-row'}>
          <div className='group-col group-sort'>{editing === null && <DragHandle />}</div>
          <div className='group-col group-name'>{category.name}</div>
          <div className='group-col group-actions'>
            <EditActions
              active={category.active}
              expanded={expanded}
              onToggleActive={onToggleActive}
              onToggleEdit={() => setEditing(expanded ? null : category.id)}
              categoryId={category.id}
            />
          </div>
        </div>
        {expanded && <BillingCategoryDetails category={category} />}
      </div>
    );
  }
  // else, creating new group
  return (
    <div className='group-collapsed'>
      <div className='group-row'>
        <div className='group-col group-sort' />
        <div className='group-col group-name'><EditableGroup category={category} /></div>
        <div className='group-col group-actions'>
          <CreateActions
            onCancel={cancelNewCategory}
            onSave={() => saveCategory(category)}
          />
        </div>
      </div>
    </div>
  );
});

const DragHandle = SortableHandle(() => (
  <span className='drag-handle'>
    <FontAwesome name='reorder' />
  </span>
));

const EditableGroup = (props) => {
  const { category } = props;

  return (
    <div className='create-group-form'>
      <input
        ref={input => input && input.focus()}
        type='text'
        defaultValue={category.name}
        placeholder='Add Group Name (required)'
        onChange={(e) => { category.name = e.target.value; }}
        className='edit-new-group-name'
      />
      <input
        type='text'
        defaultValue={category.account}
        placeholder='Add Account Name (optional)'
        onChange={(e) => { category.account = e.target.value; }}
        className='edit-new-group-account'
      />
    </div>
  );
};

EditableGroup.propTypes = {
  category: BillingCategoryType.isRequired
};

const BillingCategoryGroups = (props) => {
  const {
    categories,
    createCategory,
    updateCategory,
    onReorder,
    confirmDeactivate
  } = props;
  const [newCategory, setNewCategory] = useState(null);
  const [saveError, setSaveError] = useState(null);
  const [reordering, setReordering] = useState(false);

  const cancelNewCategory = () => {
    setNewCategory(null);
    setSaveError(null);
  };

  const saveCategory = (category) => {
    if (category.id) {
      updateCategory(category).catch((response) => setSaveError(response.errors));
    } else {
      createCategory(category).then(() => {
        setNewCategory(null);
        setSaveError(null);
      }).catch((response) => setSaveError(response.errors));
    }
  };

  const containerRef = useRef();

  return (
    <div className={`billing-category-groups ${reordering ? 'reordering' : ''}`}>
      {saveError
      && (
        <div className='alert alert-danger'>
          {saveError.map(e => <div key={e}>{e}</div>)}
        </div>
      )}
      <div>
        <LinkButton
          className='add-item-button'
          content={<FALabel icon='plus-circle' iconFirst label='Add New Billing Category Group' />}
          onClick={() => setNewCategory({ name: '', position: categories.length + 1 })}
        />
      </div>

      <div className='group-header group-row'>
        <div className='group-col group-sort' />
        <div className='group-col group-name'>Category Group Name</div>
        <div className='group-col group-actions' />
      </div>

      <BillingCategoryGroupContainer
        categories={categories}
        saveCategory={saveCategory}
        newCategory={newCategory}
        cancelNewCategory={cancelNewCategory}
        confirmDeactivate={confirmDeactivate}
        /* the following properties are all related to Sortable */
        helperClass='sortable-dragging'
        helperContainer={() => containerRef.current}
        helperContainerRef={containerRef}
        useDragHandle
        onSortEnd={({ newIndex, oldIndex }) => {
          setReordering(false);

          if (newIndex !== oldIndex) {
            onReorder(arrayMove(categories, oldIndex, newIndex));
          }
        }}
        onSortStart={() => setReordering(true)}
      />
    </div>
  );
};

BillingCategoryGroups.propTypes = {
  categories: PropTypes.arrayOf(BillingCategoryType).isRequired,
  createCategory: PropTypes.func.isRequired,
  updateCategory: PropTypes.func.isRequired,
  onReorder: PropTypes.func.isRequired,
  confirmDeactivate: PropTypes.func.isRequired
};

const mapDispatchToProps = (dispatch) => ({
  createCategory: (category) => (
    dispatch(billingCategoryGroups.actions.create(category))
      .then(() => dispatch(billingCategoryGroups.actions.markDirty('_active')))
  ),
  updateCategory: (category) => (
    dispatch(billingCategoryGroups.actions.update(category))
      .then(() => dispatch(billingCategoryGroups.actions.markDirty('_active', '_inactive')))
      .catch((error) => {
        dispatch(modals.actions.show('UnexpectedError', { message: error.errors.join(', ') || 'An error occurred while updating the category.' }));
      })
  ),
  onReorder: (categories) => (
    dispatch(billingCategoryGroups.actions.reorder(categories.map(c => c.id), '_active'))
      .catch(() => dispatch(modals.actions.show('UnexpectedError')))
  ),
  confirmDeactivate: (message, title, onConfirm) => (
    dispatch(modals.actions.show('VerificationModal', { message, title, onConfirm }))
  )
});

export default connect(null, mapDispatchToProps)(BillingCategoryGroups);
